/**
 * Backorder Dialogs Manager
 * Handles "How it Works" and "Policy Terms" dialogs for backorder products
 */
class BackorderDialogs {
    constructor() {
        this.howWorksDialog = null;
        this.policyTermsDialog = null;
        this.init();
    }
    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        }
        else {
            this.setup();
        }
    }
    setup() {
        this.setupHowWorksDialog();
        this.setupPolicyTermsDialog();
    }
    setupHowWorksDialog() {
        this.howWorksDialog = document.getElementById('backorder_dialog__howworks');
        if (!this.howWorksDialog)
            return;
        const closeButton = this.howWorksDialog.querySelector('.dialog-close-button');
        if (!closeButton) {
            console.warn('Backorder How Works dialog: close button not found');
            return;
        }
        closeButton.addEventListener('click', () => {
            this.closeDialog(this.howWorksDialog);
        });
        // Prevent closing on backdrop click
        this.howWorksDialog.addEventListener('click', (e) => {
            e.stopPropagation();
        });
        // Prevent closing with ESC key
        this.howWorksDialog.addEventListener('cancel', (e) => {
            e.preventDefault();
        });
    }
    setupPolicyTermsDialog() {
        this.policyTermsDialog = document.getElementById('backorder_dialog__policyterms');
        if (!this.policyTermsDialog)
            return;
        const termsCheckbox = this.policyTermsDialog.querySelector("input[type='checkbox']");
        const agreeButton = this.policyTermsDialog.querySelector('button.button');
        if (!termsCheckbox || !agreeButton) {
            console.warn('Backorder Policy Terms dialog: required elements not found');
            return;
        }
        termsCheckbox.addEventListener('change', () => {
            const isChecked = termsCheckbox.checked;
            agreeButton.disabled = !isChecked;
            agreeButton.setAttribute('aria-disabled', String(!isChecked));
        });
        agreeButton.addEventListener('click', () => {
            if (!termsCheckbox.checked)
                return;
            const cb = this.policyTermsPendingCallback;
            this.policyTermsPendingCallback = undefined;
            this.closeDialog(this.policyTermsDialog);
            if (typeof cb === 'function')
                cb();
        });
        // Prevent closing on backdrop click
        this.policyTermsDialog.addEventListener('click', (e) => {
            e.stopPropagation();
        });
        // Prevent closing with ESC key
        this.policyTermsDialog.addEventListener('cancel', (e) => {
            e.preventDefault();
        });
    }
    closeDialog(dialog) {
        if (dialog && typeof dialog.close === 'function') {
            dialog.close();
        }
    }
    /**
     * Opens the "How it Works" dialog
     * @param onCloseCallback - Optional callback to execute when dialog closes
     */
    openHowWorks(onCloseCallback) {
        if (this.howWorksDialog && typeof this.howWorksDialog.showModal === 'function') {
            this.howWorksDialog.showModal();
            this.trapFocus(this.howWorksDialog);
            if (typeof onCloseCallback === 'function') {
                this.howWorksDialog.addEventListener('close', onCloseCallback, { once: true });
            }
        }
    }
    /**
     * Opens the "Policy Terms" dialog
     * @param onCloseCallback - Optional callback to execute when dialog closes
     */
    openPolicyTerms(deliveryMessageOrOnClose, onCloseCallback) {
        if (!this.policyTermsDialog || typeof this.policyTermsDialog.showModal !== 'function')
            return;
        let deliveryMessage;
        let closeCb;
        if (typeof deliveryMessageOrOnClose === 'function') {
            closeCb = deliveryMessageOrOnClose;
        }
        else {
            deliveryMessage = deliveryMessageOrOnClose;
            closeCb = onCloseCallback;
        }
        this.setPolicyTermsDeliveryMessage(deliveryMessage);
        // Store callback to be executed ONLY when user clicks "Concordo"
        this.policyTermsPendingCallback = typeof closeCb === 'function' ? closeCb : undefined;
        this.policyTermsDialog.showModal();
        this.trapFocus(this.policyTermsDialog);
        // Clean up message + pending callback on close so they don't leak
        this.policyTermsDialog.addEventListener('close', () => {
            this.setPolicyTermsDeliveryMessage(undefined);
            this.policyTermsPendingCallback = undefined;
        }, { once: true });
    }
    setPolicyTermsDeliveryMessage(message) {
        if (!this.policyTermsDialog)
            return;
        const main = this.policyTermsDialog.querySelector('#backorder_policyterms_content');
        if (!main)
            return;
        const existing = main.querySelector('.backorder-delivery-message');
        if (!message) {
            if (existing)
                existing.remove();
            return;
        }
        const el = existing || document.createElement('p');
        el.classList.add('backorder-delivery-message');
        el.setAttribute('aria-live', 'polite');
        el.style.whiteSpace = 'pre-line';
        el.textContent = message;
        if (!existing)
            main.appendChild(el);
    }
    trapFocus(dialog) {
        const focusableElements = this.getFocusableElements(dialog);
        if (focusableElements.all.length === 0)
            return;
        focusableElements.first.focus();
        const handleTabKey = (e) => {
            if (e.key !== 'Tab')
                return;
            if (e.shiftKey) {
                if (document.activeElement === focusableElements.first) {
                    e.preventDefault();
                    focusableElements.last.focus();
                }
            }
            else {
                if (document.activeElement === focusableElements.last) {
                    e.preventDefault();
                    focusableElements.first.focus();
                }
            }
        };
        dialog.addEventListener('keydown', handleTabKey);
        dialog.addEventListener('close', () => {
            dialog.removeEventListener('keydown', handleTabKey);
        }, { once: true });
    }
    getFocusableElements(dialog) {
        const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const elements = Array.from(dialog.querySelectorAll(selector));
        return {
            all: elements,
            first: elements[0],
            last: elements[elements.length - 1]
        };
    }
}
// Initialize and expose globally
if (typeof window !== 'undefined') {
    window.BackorderDialogs = new BackorderDialogs();
}
